import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import AppShell from "@/pages/AppShell";
import Dashboard from "@/pages/Dashboard";
import Groups from "@/pages/Groups";
import Tags from "@/pages/Tags";
import Settings from "@/pages/Settings";
import PublicProfile from "@/pages/PublicProfile";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-500 text-sm tracking-[0.2em] uppercase pulse-soft">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous check for session_id in URL fragment (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingOrDashboard />} />
      <Route path="/u/:username" element={<PublicProfileWithChrome />} />
      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/groups" element={<Groups />} />
        <Route path="/tags" element={<Tags />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function PublicProfileWithChrome() {
  // If user is logged in, render inside the AppShell; otherwise render a minimal page.
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-500 text-sm tracking-[0.2em] uppercase pulse-soft">Loading…</div>
      </div>
    );
  }
  if (user) {
    return <AppShell><PublicProfile /></AppShell>;
  }
  return (
    <div className="min-h-screen">
      <main className="max-w-[1400px] mx-auto px-6 py-10 fade-up">
        <PublicProfile />
        <div className="mt-12 text-center text-xs text-slate-500">
          Want a profile of your own? <a href="/" className="text-[#E11D48] hover:underline">Sign in</a> to start your backlog.
        </div>
      </main>
    </div>
  );
}

function LandingOrDashboard() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-500 text-sm tracking-[0.2em] uppercase pulse-soft">Loading…</div>
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster theme="dark" position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
